import type { Resolution } from './simulation/constants.ts';

export type TerrainPreset =
  | 'mountains'
  | 'rollingHills'
  | 'plateau'
  | 'canyon'
  | 'dunes'
  | 'flat';

/**
 * UI strings for the presets.
 *
 * The `id` values are the simulation's vocabulary and stay English; only the
 * labels and hints are localised, so a preset can never be renamed by a
 * translation.
 */
export const TERRAIN_PRESETS: { id: TerrainPreset; label: string; hint: string }[] = [
  { id: 'mountains', label: '山脉', hint: '脊状分形噪声叠加域扭曲，峰脊尖锐' },
  { id: 'rollingHills', label: '丘陵', hint: '平滑分形噪声，坡度平缓' },
  { id: 'plateau', label: '高原', hint: '顶部平坦，边缘为陡崖' },
  { id: 'canyon', label: '峡谷', hint: '高原被蜿蜒河道切开' },
  { id: 'dunes', label: '沙丘', hint: '各向异性波纹，适合风蚀研究' },
  { id: 'flat', label: '平地（测试）', hint: '近水平微倾斜面，几乎无起伏' },
];

/* -------------------------------------------------------------------------- */
/* 参数分组                                                                    */
/* -------------------------------------------------------------------------- */

export interface TerrainParams {
  preset: TerrainPreset;
  resolution: Resolution;
  /** Cell edge length in metres. World extent = resolution * cellSize. */
  cellSize: number;
  seed: number;
  /** Domain scale in noise units; larger = fewer, bigger landforms. */
  scale: number;
  /** Peak elevation of the generated terrain [m]. */
  height: number;
  /** FBM octave gain; higher = rougher small-scale relief. 0..1 */
  roughness: number;
  /** Ridged-noise exponent; higher = sharper crests. 1..4 */
  sharpness: number;
  /** Domain warp amplitude in noise units. 0..2 */
  warp: number;
}

export interface HydraulicParams {
  enabled: boolean;
  /** Rain intensity, metres of water per simulated hour. */
  rainfall: number;
  /** Free-surface conductance K, clamped to the grid's stability bound. */
  flowRate: number;
  /** Flow resistance; keeps the velocity diagnosis stable at large dt. */
  flowDamping: number;
  /**
   * Water-transport sub-steps per two-second reference interval.
   *
   * The runtime scales the count with `dtSim`, keeping sub-step duration stable.
   * Each sub-step's advection reads the field the previous one wrote, so
   * sub-stepping is both what lets the flux field turn around within a tick and
   * what keeps the donor-cell CFL number at or below one. It costs one grid
   * sweep (CPU) or one dispatch (GPU) each, so it is a direct
   * accuracy-versus-cost dial.
   */
  flowSubsteps: number;
  /** Soil erodibility multiplier in the sediment-capacity law. */
  erosionStrength: number;
  /** Sediment capacity coefficient [m^-1 s]. */
  sedimentCapacity: number;
  /** Reference two-second fraction of excess suspended load that settles. 0..1 */
  depositionRate: number;
  /** Water lost per simulated hour [m]. */
  evaporation: number;
  /** Maximum transfer from surface water into regolith storage [m/hour]. */
  infiltration: number;
  /** Fraction of groundwater storage returned to the surface per hour. */
  baseflow: number;
  /** Hard cap on net elevation change per two-second reference interval [m]. */
  maxErosionPerTick: number;
}

export interface GravityParams {
  enabled: boolean;
  /** Angle of repose; slopes steeper than this collapse. [degrees] */
  talusAngle: number;
  /** Reference two-second fraction of excess slope material moved. 0..1 */
  collapseRate: number;
  /** Jacobi relaxation sweeps per two-second reference interval. */
  iterations: number;
}

export interface WindParams {
  enabled: boolean;
  /** Meteorological direction the wind blows *towards*, degrees CCW from +x. */
  direction: number;
  /** Wind speed at 10 m reference height [m/s]. */
  speed: number;
  /** Loose-sediment entrainment coefficient. */
  erosion: number;
  /** Downwind hop length in cells. */
  transportDistance: number;
  /** Deposition coefficient inside wind shadows. */
  deposition: number;
}

export interface EngineParams {
  /** Simulation ticks executed per rendered frame (at 1x). */
  iterationsPerFrame: number;
  /** Wall-clock time multiplier. */
  timeScale: number;
  /** Simulated seconds advanced by one tick. */
  dtSim: number;
}

export interface SimParams {
  terrain: TerrainParams;
  hydraulic: HydraulicParams;
  gravity: GravityParams;
  wind: WindParams;
  engine: EngineParams;
}

/* -------------------------------------------------------------------------- */
/* 数值范围：同时驱动 UI 滑块与 GPU uniform 上传                               */
/* -------------------------------------------------------------------------- */

export interface Range {
  min: number;
  max: number;
  step: number;
  /** Optional display unit. */
  unit?: string;
  /** Localised label. */
  label: string;
  /** One-line explanation shown as the control's tooltip. */
  hint?: string;
}

export const LIMITS = {
  terrain: {
    cellSize: { min: 2, max: 40, step: 1, unit: '米', label: '网格尺寸', hint: '世界范围 = 分辨率 × 网格尺寸' },
    seed: { min: 0, max: 99999, step: 1, label: '随机种子' },
    scale: { min: 0.5, max: 8, step: 0.05, label: '地貌尺度', hint: '噪声域大小；越大，地貌越宽阔' },
    height: { min: 50, max: 3000, step: 10, unit: '米', label: '高度' },
    roughness: { min: 0.1, max: 0.75, step: 0.01, label: '粗糙度', hint: '分形噪声各倍频的振幅增益' },
    sharpness: { min: 1, max: 4, step: 0.05, label: '尖锐度', hint: '脊状噪声指数；越大山脊越尖' },
    warp: { min: 0, max: 2, step: 0.02, label: '域扭曲', hint: '域扭曲振幅；让地形呈现被揉皱的褶皱感' },
  },
  hydraulic: {
    rainfall: { min: 0, max: 0.6, step: 0.005, unit: '米/时', label: '降雨强度', hint: '降雨强度；除笔刷注水外唯一的水源' },
    flowRate: { min: 0, max: 4, step: 0.02, label: '流动响应', hint: '自由水面导水系数 K，会被网格稳定性上限截断' },
    flowDamping: { min: 0.01, max: 1, step: 0.01, label: '流动阻尼', hint: '流速诊断中的阻力；越大越黏滞' },
    flowSubsteps: { min: 1, max: 16, step: 1, label: '输运子步数', hint: '每 tick 把水流输运拆成几个子步；dt 越大越需要更多子步，代价成正比' },
    erosionStrength: { min: 0, max: 8, step: 0.05, label: '侵蚀强度', hint: '河床可蚀性，会除以当地硬度' },
    sedimentCapacity: { min: 0, max: 0.5, step: 0.002, label: '泥沙容量', hint: '流速越快、坡度越陡，水流能携带的泥沙越多' },
    depositionRate: { min: 0, max: 1, step: 0.01, label: '沉积速率', hint: '每 tick 沉降掉的超载泥沙比例' },
    evaporation: { min: 0, max: 0.6, step: 0.005, unit: '米/时', label: '蒸发', hint: '每小时蒸发损失的水量' },
    infiltration: { min: 0, max: 0.6, step: 0.005, unit: '米/时', label: '入渗', hint: '风化层吸收坡面水的最大速率；裸岩不入渗' },
    baseflow: { min: 0, max: 1, step: 0.01, unit: '/时', label: '基流', hint: '每小时从地下水储量回补地表的比例' },
    maxErosionPerTick: { min: 0.05, max: 6, step: 0.05, unit: '米', label: '单 tick 最大 Δh', hint: '每 tick 允许的最大高程变化' },
  },
  gravity: {
    talusAngle: { min: 5, max: 70, step: 0.5, unit: '°', label: '休止角', hint: '自然休止角；超过该坡度的坡面会坍塌' },
    collapseRate: { min: 0, max: 1, step: 0.01, label: '坍塌速率', hint: '每次迭代搬运的超额坡面物质比例' },
    iterations: { min: 1, max: 6, step: 1, label: '迭代次数', hint: '每 tick 的松弛扫描次数' },
  },
  wind: {
    direction: { min: 0, max: 360, step: 1, unit: '°', label: '风向', hint: '风的去向，自 +x 轴逆时针度量' },
    speed: { min: 0, max: 40, step: 0.5, unit: '米/秒', label: '风速', hint: '低于阈值风速时沙粒不动；超过后起沙量随风速超出部分增长' },
    erosion: { min: 0, max: 4, step: 0.02, label: '风蚀强度', hint: '松散物质的起沙系数' },
    transportDistance: { min: 1, max: 48, step: 1, unit: '格', label: '跃移距离', hint: '沙粒一次跃移的跳距' },
    deposition: { min: 0, max: 1, step: 0.01, label: '沉积', hint: '风影区内沉降下来的载荷比例' },
  },
  engine: {
    iterationsPerFrame: { min: 1, max: 8, step: 1, label: '每帧 tick 上限', hint: '每渲染帧最多推进的 tick 数；视口始终按帧率渲染' },
    dtSim: { min: 0.1, max: 30, step: 0.1, unit: '秒', label: '每 tick 模拟秒数' },
  },
} satisfies Record<string, Record<string, Range>>;

/* -------------------------------------------------------------------------- */
/* 默认值                                                                      */
/* -------------------------------------------------------------------------- */

export const DEFAULT_PARAMS: SimParams = {
  terrain: {
    preset: 'mountains',
    resolution: 256,
    cellSize: 8,
    seed: 1337,
    scale: 2.4,
    height: 1400,
    roughness: 0.5,
    sharpness: 1.45,
    warp: 0.55,
  },
  hydraulic: {
    enabled: true,
    rainfall: 0.09,
    flowRate: 0.55,
    flowDamping: 0.35,
    flowSubsteps: 4,
    erosionStrength: 1.6,
    sedimentCapacity: 0.06,
    depositionRate: 0.3,
    evaporation: 0.02,
    infiltration: 0.06,
    baseflow: 0.04,
    maxErosionPerTick: 1.5,
  },
  gravity: {
    enabled: true,
    talusAngle: 34,
    collapseRate: 0.4,
    iterations: 2,
  },
  wind: {
    enabled: false,
    direction: 45,
    speed: 14,
    erosion: 0.6,
    transportDistance: 6,
    deposition: 0.45,
  },
  engine: {
    iterationsPerFrame: 2,
    timeScale: 1,
    dtSim: 2,
  },
};

/** Per-preset tuning overrides, so each starting terrain is interesting. */
export const PRESET_OVERRIDES: Partial<Record<TerrainPreset, Partial<TerrainParams>>> = {
  mountains: {},
  rollingHills: { scale: 3.2, height: 420, roughness: 0.45, sharpness: 1.6, warp: 0.3 },
  plateau: { scale: 2.0, height: 900, roughness: 0.42, sharpness: 2.6, warp: 0.25 },
  canyon: { scale: 2.2, height: 780, roughness: 0.5, sharpness: 2.2, warp: 0.5 },
  dunes: { scale: 4.0, height: 180, roughness: 0.55, sharpness: 1.4, warp: 0.2 },
  flat: { scale: 3.0, height: 120, roughness: 0.2, sharpness: 1.0, warp: 0.0 },
};

export function cloneParams(p: SimParams): SimParams {
  return {
    terrain: { ...p.terrain },
    hydraulic: { ...p.hydraulic },
    gravity: { ...p.gravity },
    wind: { ...p.wind },
    engine: { ...p.engine },
  };
}

export function defaultParamsFor(preset: TerrainPreset): SimParams {
  const p = cloneParams(DEFAULT_PARAMS);
  p.terrain.preset = preset;
  Object.assign(p.terrain, PRESET_OVERRIDES[preset] ?? {});
  if (preset === 'flat') {
    p.gravity.talusAngle = 30;
    p.hydraulic.rainfall = 0.12;
  }
  return p;
}
